import * as AppliedModifiersConstants from '../constant/applied_modifiers_constants';
import { AppliedModifierFormData, ChargeByItem, ChargeByTabId } from '../constant/applied_modifiers_constants';

/**
 * Validation result interface
 */
export interface ValidationResult {
    isValid: boolean;
}

/**
 * Applied Modifier Validator utility class
 * Centralizes all validation logic for Applied Modifier forms
 */
export class AppliedModifierValidator {
    
    /**
     * Validates a single field based on field identifier
     * @param fieldName - The field identifier from AppliedModifiersConstants
     * @param formData - The complete form data
     * @param isSubmitted - Whether the form has been submitted
     * @returns ValidationResult with validation status
     */
    static validateField(fieldName: string, formData: AppliedModifierFormData, isSubmitted: boolean = false): ValidationResult {
        switch (fieldName) {
            case AppliedModifiersConstants.field_identifier.setting_name:
                return this.validateSettingName(formData.setting_name);
            
            case AppliedModifiersConstants.field_identifier.active_status:
                return this.validateActiveStatus(formData.active_status);
            
            case AppliedModifiersConstants.field_identifier.modifier:
                return this.validateModifier(formData.modifier);
            
            case AppliedModifiersConstants.field_identifier.to_charge_by:
                return this.validateToChargeBy(formData.to_charge_by);
            
            case AppliedModifiersConstants.field_identifier.date_of_service_range:
                return this.validateDateOfServiceRange(formData.start_date, formData.end_date);
            
            case AppliedModifiersConstants.field_identifier.primary_specialty:
                return this.validatePrimarySpecialty(formData.primary_specialty, formData.provider_type);
            
            case AppliedModifiersConstants.field_identifier.provider_type:
                return this.validateProviderType(formData.provider_type, formData.primary_specialty);
            
            case AppliedModifiersConstants.field_identifier.tabs_wrapper:
                return this.validateTabsWrapper(formData.payer, formData.insurance_class, formData.excluded_insurance_class);
            
            default:
                return { isValid: true };
        }
    }

    /**
     * Validates setting name field
     */
    static validateSettingName(settingName: string): ValidationResult {
        if (!settingName || settingName.trim() === '') {
            return { isValid: false };
        }
        return { isValid: true };
    }

    /**
     * Validates active status field
     */
    static validateActiveStatus(activeStatus: boolean): ValidationResult {
        if (activeStatus === undefined || activeStatus === null) {
            return { isValid: false };
        }
        return { isValid: true };
    }

    /**
     * Validates modifier field
     */
    static validateModifier(modifier: string): ValidationResult {
        if (!modifier || modifier.trim() === '' || modifier.trim() === '0') {
            return { isValid: false };
        }
        return { isValid: true };
    }

    /**
     * Validates to_charge_by field
     */
    static validateToChargeBy(toChargeBy: ChargeByItem[]): ValidationResult {
        const isValid = toChargeBy && 
                       Array.isArray(toChargeBy) && 
                       toChargeBy.length > 0 &&
                       toChargeBy.every(item => 
                           item && 
                           typeof item === 'object' && 
                           item.label !== null && 
                           item.value !== null && 
                           (item.tabId === ChargeByTabId.RevenueCenter || item.tabId === ChargeByTabId.ProcedureCode)
                       );
        
        return { isValid };
    }

    /**
     * Validates date of service range
     */
    static validateDateOfServiceRange(startDate: string, endDate: string): ValidationResult {
        if (!startDate) {
            return { isValid: false };
        }
        return { isValid: true };
    }

    /**
     * Validates primary specialty (conditional based on provider type)
     */
    static validatePrimarySpecialty(primarySpecialty: any[], providerType: string): ValidationResult {
        const isProviderTypeSelected = providerType && providerType !== AppliedModifiersConstants.ProviderTypeValues.NONE;
        
        if (isProviderTypeSelected) {
            if (!primarySpecialty || !Array.isArray(primarySpecialty) || primarySpecialty.length === 0) {
                return { isValid: false };
            }
        }
        return { isValid: true };
    }

    /**
     * Validates provider type (conditional based on primary specialty)
     */
    static validateProviderType(providerType: string, primarySpecialty: any[]): ValidationResult {
        const isPrimarySpecialtySelected = primarySpecialty && Array.isArray(primarySpecialty) && primarySpecialty.length > 0;
        
        if (isPrimarySpecialtySelected) {
            if (!providerType || providerType === AppliedModifiersConstants.ProviderTypeValues.NONE) {
                return { isValid: false };
            }
        }
        return { isValid: true };
    }

    /**
     * Validates tabs wrapper (at least one tab must have selections)
     */
    static validateTabsWrapper(payer: any[], insuranceClass: any[], excludedInsuranceClass: any[]): ValidationResult {
        const hasPayerSelection = payer && Array.isArray(payer) && payer.length > 0;
        const hasInsuranceClassSelection = insuranceClass && Array.isArray(insuranceClass) && insuranceClass.length > 0;
        const hasExcludedInsuranceClassSelection = excludedInsuranceClass && Array.isArray(excludedInsuranceClass) && excludedInsuranceClass.length > 0;
        
        const isValid = hasPayerSelection || hasInsuranceClassSelection || hasExcludedInsuranceClassSelection;
        return { isValid };
    }

    /**
     * Validates all required fields for the form
     * @param formData - The complete form data
     * @param baseRequiredFields - Array of base required field identifiers
     * @returns ValidationResult with overall form validation status
     */
    static validateForm(formData: AppliedModifierFormData, baseRequiredFields: string[]): ValidationResult {
        // Get all required fields including conditional ones
        const requiredFields = [...baseRequiredFields];
        
        // Add conditional required fields
        const isProviderTypeSelected = formData.provider_type && formData.provider_type !== AppliedModifiersConstants.ProviderTypeValues.NONE;
        const isPrimarySpecialtySelected = formData.primary_specialty && Array.isArray(formData.primary_specialty) && formData.primary_specialty.length > 0;
        
        if (isProviderTypeSelected) {
            requiredFields.push(AppliedModifiersConstants.field_identifier.primary_specialty);
        }
        
        if (isPrimarySpecialtySelected) {
            requiredFields.push(AppliedModifiersConstants.field_identifier.provider_type);
        }

        // Validate all required fields
        for (const fieldName of requiredFields) {
            const result = this.validateField(fieldName, formData, true);
            if (!result.isValid) {
                return result;
            }
        }

        return { isValid: true };
    }

    /**
     * Checks if a field should show required validation styling
     * @param fieldName - The field identifier
     * @param formData - The complete form data
     * @param isSubmitted - Whether the form has been submitted
     * @returns boolean indicating if field should show required validation
     */
    static shouldShowRequiredValidation(fieldName: string, formData: AppliedModifierFormData, isSubmitted: boolean): boolean {
        if (!isSubmitted) return false;
        
        const result = this.validateField(fieldName, formData, isSubmitted);
        return !result.isValid;
    }


}
